import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { getUsers, createPlanner } from '../../api/users';

type UserItem = {
  id: number;
  loginId: string;
  nickname: string;
  userName: string;
  email?: string | null;
  phoneNumber?: string | null;
  userType: string;
  activeYn: number;
  creationTime?: string;
};

type UserListData = {
  list?: UserItem[];
  rows?: UserItem[];
  totalCount?: number;
  count?: number;
};

type UserListResponse = {
  status: number;
  message: string;
  data: UserListData | UserItem[];
};

type SearchFormValues = {
  keyword?: string;
  activeYn?: number;
};

type CreatePlannerFormValues = {
  loginId: string;
  password: string;
  nickname: string;
  userName: string;
  email?: string;
  phoneNumber?: string;
  activeYn?: number;
};

export default function PlannerListPage() {
  const [searchForm] = Form.useForm<SearchFormValues>();
  const [createForm] = Form.useForm<CreatePlannerFormValues>();

  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [list, setList] = useState<UserItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchUsers = async ({
    page = 1,
    rows = 20,
  }: {
    page?: number;
    rows?: number;
  } = {}) => {
    try {
      setLoading(true);

      const values = searchForm.getFieldsValue();

      const result = (await getUsers({
        userType: 'PLANNER',
        activeYn: values.activeYn,
        keyword: values.keyword,
        page,
        rows,
      })) as UserListResponse;

      const rawData = result?.data;
      const items = Array.isArray(rawData)
        ? rawData
        : rawData?.list || rawData?.rows || [];
      const total = Array.isArray(rawData)
        ? rawData.length
        : rawData?.totalCount || rawData?.count || 0;

      setList(items);
      setPagination({
        current: page,
        pageSize: rows,
        total,
      });
    } catch (error: any) {
      message.error(error?.message || '플래너 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers({
      page: 1,
      rows: 20,
    });
  }, []);

  const handleSearch = async () => {
    await fetchUsers({
      page: 1,
      rows: pagination.pageSize,
    });
  };

  const handleReset = async () => {
    searchForm.resetFields();

    await fetchUsers({
      page: 1,
      rows: pagination.pageSize,
    });
  };

  const handleTableChange = async (nextPagination: TablePaginationConfig) => {
    await fetchUsers({
      page: nextPagination.current || 1,
      rows: nextPagination.pageSize || 20,
    });
  };

  const handleOpenCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      activeYn: 1,
    });
    setOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
  };

  const handleCreatePlanner = async () => {
    try {
      const values = await createForm.validateFields();

      setCreateLoading(true);

      await createPlanner({
        loginId: values.loginId.trim(),
        password: values.password,
        nickname: values.nickname.trim(),
        userName: values.userName.trim(),
        email: values.email?.trim() || undefined,
        phoneNumber: values.phoneNumber?.trim() || undefined,
        activeYn: values.activeYn ?? 1,
      });

      message.success('플래너가 생성되었습니다.');
      setOpenCreateModal(false);
      createForm.resetFields();

      await fetchUsers({
        page: 1,
        rows: pagination.pageSize,
      });
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      message.error(error?.message || '플래너 생성 중 오류가 발생했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  const columns: ColumnsType<UserItem> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '이름',
      dataIndex: 'userName',
      width: 140,
    },
    {
      title: '닉네임',
      dataIndex: 'nickname',
      width: 140,
    },
    {
      title: '로그인 ID',
      dataIndex: 'loginId',
      width: 180,
    },
    {
      title: '이메일',
      dataIndex: 'email',
      width: 220,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: '연락처',
      dataIndex: 'phoneNumber',
      width: 160,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: '유형',
      dataIndex: 'userType',
      width: 120,
      render: (value: string) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: '상태',
      dataIndex: 'activeYn',
      width: 100,
      render: (value: number) =>
        Number(value) === 1 ? <Tag color="green">활성</Tag> : <Tag>비활성</Tag>,
    },
    {
      title: '생성일',
      dataIndex: 'creationTime',
      width: 180,
      render: (value: string | undefined) => value || '-',
    },
  ];
  
  return (
    <>
      <Card
        title="플래너 관리"
        extra={
          <Button type="primary" onClick={handleOpenCreateModal}>
            플래너 등록
          </Button>
        }
      >
        <Form<SearchFormValues>
          form={searchForm}
          layout="inline"
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              allowClear
              placeholder="이름 / 닉네임 / 로그인ID 검색"
              style={{ width: 240 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>

          <Form.Item name="activeYn">
            <Select
              allowClear
              placeholder="활성 여부"
              style={{ width: 140 }}
              options={[
                { label: '활성', value: 1 },
                { label: '비활성', value: 0 },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                검색
              </Button>
              <Button onClick={handleReset}>초기화</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<UserItem>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={list}
          scroll={{ x: 1300 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title="플래너 등록"
        open={openCreateModal}
        onCancel={handleCloseCreateModal}
        onOk={handleCreatePlanner}
        okText="등록"
        cancelText="취소"
        confirmLoading={createLoading}
        destroyOnHidden
      >
        <Form<CreatePlannerFormValues>
          form={createForm}
          layout="vertical"
          initialValues={{
            activeYn: 1,
          }}
        >
          <Form.Item
            label="로그인 ID"
            name="loginId"
            rules={[
              { required: true, message: '로그인 ID를 입력해주세요.' },
              { min: 4, message: '로그인 ID는 4자 이상 입력해주세요.' },
            ]}
          >
            <Input placeholder="로그인 ID 입력" />
          </Form.Item>

          <Form.Item
            label="비밀번호"
            name="password"
            rules={[
              { required: true, message: '비밀번호를 입력해주세요.' },
              { min: 4, message: '비밀번호는 4자 이상 입력해주세요.' },
            ]}
          >
            <Input.Password placeholder="비밀번호 입력" />
          </Form.Item>

          <Form.Item
            label="이름"
            name="userName"
            rules={[{ required: true, message: '이름을 입력해주세요.' }]}
          >
            <Input placeholder="이름 입력" />
          </Form.Item>

          <Form.Item
            label="닉네임"
            name="nickname"
            rules={[{ required: true, message: '닉네임을 입력해주세요.' }]}
          >
            <Input placeholder="닉네임 입력" />
          </Form.Item>

          <Form.Item
            label="이메일"
            name="email"
            rules={[{ type: 'email', message: '올바른 이메일 형식이 아닙니다.' }]}
          >
            <Input placeholder="이메일 입력" />
          </Form.Item>

          <Form.Item label="연락처" name="phoneNumber">
            <Input placeholder="연락처 입력" />
          </Form.Item>

          <Form.Item label="활성 여부" name="activeYn">
            <Select
              options={[
                { label: '활성', value: 1 },
                { label: '비활성', value: 0 },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}